import React, { useState } from "react";
import { withStyles, Typography, IconButton, Container, Card, Box } from "@material-ui/core";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import useConstructor from "../../core/hooks/useConstructor";
import { FETCH_BUSINESS_EVENT_BY_ID } from "../../Fedx";
import BusinessEvent from "../../core/models/BusinessEvent";
import { Workflow } from "../../core/models/Enum/Workflow";
import { EventStatus } from "../../core/models/Enum/EventStatus";
import { useHistory, useParams } from "react-router-dom";
import { useHubXSubscribe } from "../../HubX";
import TimelineBarIcon from "../../assets/icons/TimelineBarLogo";
import EventStep from "../../core/models/EventStep";
import EventStepCommentSection from "../EventStepCommentSection";
import { EventActionStepTypes } from "../../core/models/Enum/EventActionStep";
import { ChargeStatusTypes } from "../../core/models/Enum/ChargeStatus";
import {
  TEMPLATE_PASSOS_FLUXO_6,
  TEMPLATE_FLUXOS_PRAGAS,
} from "../../core/models/Helpers/BusinessEventStepsTemplate";
import { BUSINESS_EVENT_UPDATED } from "../../Events";
import { get } from "lodash";
import { moveItem } from "../../helpers/array";

interface IEventStepLabelsObject {
  [index: number]: string | IEventStepLabelsObject;
}

const EventStepLabels: IEventStepLabelsObject = {
  0: "Disparo do Evento",
  1: "Cliente Notificado",
  2: {
    0: "Aguardando Aprovação",
    1: "Cobrança Aprovada",
    2: "Cobrança Rejeitada",
  },
  3: {
    0: "Aguardando Confirmação",
    1: "Cobrança Aprovada",
    2: "Cobrança Rejeitada",
  },
  4: "Pedido Atendido",
  5: { 0: "Encerramento da Ocorrência", 1: "Ocorrência Encerrada" },
  6: { 0: "Isenção Automática da Cobrança", 1: "Isenção Automática da Cobrança" },
};

const singleLabelStepsList = [0, 1, 4];

const determineArrowLabel = (
  currentStep: EventStep,
  mostRecentStep: number,
  skippedStep: boolean
) => {
  const { etapa_evento, status_cobranca } = currentStep;
  const correctLabel = singleLabelStepsList.includes(etapa_evento)
    ? EventStepLabels[etapa_evento]
    : etapa_evento === EventActionStepTypes.APROVACAO ||
      etapa_evento === EventActionStepTypes.CONFIRMACAO
    ? determineAppropriateChargeStepLabel(
        etapa_evento,
        status_cobranca,
        mostRecentStep,
        skippedStep
      )
    : determineEndingStepLabel(etapa_evento, mostRecentStep);
  return correctLabel.toString();
};

const determineAppropriateChargeStepLabel = (
  etapa_evento: number,
  status_cobranca: number,
  mostRecentStep: number,
  skippedApprovalStep: boolean
) => {
  return mostRecentStep > etapa_evento
    ? status_cobranca === 3
      ? EventStepLabels[etapa_evento][1]
      : EventStepLabels[etapa_evento][2]
    : skippedApprovalStep
    ? EventStepLabels[etapa_evento - 1][0]
    : EventStepLabels[etapa_evento][0];
};

const determineEndingStepLabel = (etapa_evento: number, mostRecentStep: number) => {
  return mostRecentStep === etapa_evento
    ? EventStepLabels[etapa_evento][1]
    : EventStepLabels[etapa_evento][0];
};

const WhiteTextTypography = withStyles({
  root: {
    color: "#FFFFFF",
  },
})(Typography);

const RedTextTypography = withStyles({
  root: {
    color: "#eb4034",
    fontSize: "small",
    fontWeight: "bold",
  },
})(Typography);

type EventTimelineComponentProps = {
  businessEvent: BusinessEvent;
  loading?: boolean;
  eventSteps: EventStep[];
};

function EventTimelineComponent({
  businessEvent,
  loading = false,
  eventSteps,
}: EventTimelineComponentProps) {
  const [selectedButton, setSelectedButton] = useState<number>(0);

  const templateWorkflowStepsArray =
    businessEvent?.tipo_workflow === 6
      ? TEMPLATE_PASSOS_FLUXO_6
      : TEMPLATE_FLUXOS_PRAGAS;

  const [businessSteps, setBusinessSteps] = useState<EventStep[]>(templateWorkflowStepsArray);
  const mostRecentStep = businessEvent?.etapas_registradas.reduce(
    (x, y) =>
      x.etapa_evento === 5 ? x : y.etapa_evento === 5 ? y : x.etapa_evento > y.etapa_evento ? x : y,
    get(businessEvent, "etapas_registradas[0]")
  )?.etapa_evento;

  const currentStep = businessEvent?.etapas_registradas.find(
    (er) => er.etapa_evento === mostRecentStep
  );

  const skippedStep = !businessEvent?.etapas_registradas.some(
    (x) => x.etapa_evento === EventActionStepTypes.APROVACAO
  );

  const registeredEventStepsList = eventSteps ?? [];

  useConstructor(() => {
    let EventsArray = registeredEventStepsList
      .sort((a, b) => a.etapa_evento - b.etapa_evento)
      .concat(templateWorkflowStepsArray.filter((x) => x.etapa_evento > mostRecentStep));

    const freeIndex = EventsArray.findIndex((ev) => ev.etapa_evento === 6);
    if (freeIndex > -1) {
      EventsArray = moveItem(EventsArray, freeIndex, freeIndex - 1);
    }
    setBusinessSteps(EventsArray);
    setSelectedButton(mostRecentStep);
  });

  React.useEffect(() => {
    const mostRecentStepUpdated = eventSteps.reduce(
      (x, y) =>
        x.etapa_evento === 5
          ? x
          : y.etapa_evento === 5
          ? y
          : x.etapa_evento > y.etapa_evento
          ? x
          : y,
      get(businessEvent, "etapas_registradas[0]")
    )?.etapa_evento;
    let newEventsArray = eventSteps
      .sort((a, b) => a.etapa_evento - b.etapa_evento)
      .concat(templateWorkflowStepsArray.filter((x) => x.etapa_evento > mostRecentStepUpdated));

    const freeIndex = newEventsArray.findIndex((ev) => ev.etapa_evento === 6);
    if (freeIndex > -1) {
      newEventsArray = moveItem(newEventsArray, freeIndex, freeIndex - 1);
    }
    setBusinessSteps(newEventsArray);
  }, [eventSteps, templateWorkflowStepsArray]);

  const handleClick = (statusIndex: number) => {
    setSelectedButton(statusIndex);
  };

  const getTextColor = (arrow: string) => {
    if (arrow === "#236374" || arrow === "#8BADB7") return "white";
    else return "black";
  };

  const updateArrowsColor = (etapa_evento: number) => {
    return etapa_evento === 6
      ? "#8BADB7"
      : etapa_evento === mostRecentStep
      ? "#236374"
      : etapa_evento < mostRecentStep
      ? "#8BADB7"
      : "#ccd4cb";
  };

  const getStyle = (etapa_evento: number) => {
    return etapa_evento === selectedButton
      ? { marginRight: "-8px", marginLeft: "-11px", width: "190px" }
      : { marginRight: "-6px", marginLeft: "-6px" };
  };

  const timelineStepComponent = (
    step: EventStep,
    index: number,
    skippedConfirmationStep: boolean
  ) => {
    const { etapa_evento } = step;
    return (
      <Box p={0} m={0} key={`box_${index}`}>
        <IconButton
          value={etapa_evento}
          onClick={() => handleClick(step.etapa_evento)}
          style={{ padding: "0px" }}
          key={`iconbutton_${index}`}
        >
          <TimelineBarIcon
            style={getStyle(etapa_evento)}
            fillColor={updateArrowsColor(step.etapa_evento)}
            textColor={getTextColor(updateArrowsColor(step.etapa_evento))}
            barText={determineArrowLabel(step, mostRecentStep, skippedConfirmationStep)}
            key={`timelineIcon_${index}`}
          />
        </IconButton>
        <Box
          p={0}
          fontSize="11px"
          textAlign="center"
          maxWidth="137px"
          color="#606060"
          display="flex"
          justifyContent="space-around"
          flexDirection="column"
        >
          {(step.etapa_evento === EventActionStepTypes.APROVACAO ||
            step.etapa_evento === EventActionStepTypes.CONFIRMACAO) &&
          (step.status_cobranca === ChargeStatusTypes.AGUARDANDO_APROVACAO_PARA_EMISSAO ||
            step.status_cobranca === ChargeStatusTypes.AGUARDANDO_CONFIRMACAO_DE_NAO_EMISSAO) ? (
            ""
          ) : (
            <>
              <text>{step.updated_at_formatted}</text>{" "}
              <text>{step.aprovador ? `por ${step.aprovador}` : ""}</text>
            </>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <>
      <Container maxWidth={"xl"}>
        <Card>
          <div>
            <Box p={1} flexGrow={1} bgcolor="#6B717F">
              <WhiteTextTypography>Status do Pedido</WhiteTextTypography>
            </Box>
          </div>
          <div style={{ width: "100%" }}>
            {currentStep?.status_cobranca === ChargeStatusTypes.AGUARDANDO_APROVACAO_PARA_EMISSAO ||
            currentStep?.status_cobranca ===
              ChargeStatusTypes.AGUARDANDO_CONFIRMACAO_DE_NAO_EMISSAO ? (
              <div>
                <Box p={1} flexGrow={1} bgcolor="background.paper">
                  {currentStep?.status_cobranca ===
                  ChargeStatusTypes.AGUARDANDO_CONFIRMACAO_DE_NAO_EMISSAO ? (
                    <RedTextTypography>
                      Aprovador Pendente:{" "}
                      {(businessEvent.cliente?.email_diretor?.nome ?? businessEvent.cliente_sap?.email_diretor?.nome) || "Nenhum Diretor definido"}
                    </RedTextTypography>
                  ) : (
                    <RedTextTypography>
                      Aprovador Pendente:{" "}
                      {(businessEvent.cliente?.email_gerente_regional?.nome ?? businessEvent.cliente_sap?.email_gerente_regional?.nome) ||
                        (businessEvent.cliente?.email_diretor?.nome ?? businessEvent.cliente_sap?.email_diretor?.nome) ||
                        "Nenhum Gerente Regional ou Diretor definido"}
                    </RedTextTypography>
                  )}
                </Box>
              </div>
            ) : (
              ""
            )}
            {registeredEventStepsList.filter((x) => x.etapa_evento === 6).length > 0 ? (
              <div>
                <Box p={1} flexGrow={1} bgcolor="background.paper">
                  <RedTextTypography>
                    Isenção automática da cobrança, pois cliente está configurado como exceção
                  </RedTextTypography>
                </Box>
              </div>
            ) : (
              ""
            )}
            <Box
              display="flex"
              flexDirection="row"
              alignItems="flex-start"
              justifyContent="center"
              p={3}
              m={0}
              bgcolor="background.paper"
            >
              {businessSteps.map((step, idx) => timelineStepComponent(step, idx, skippedStep))}
            </Box>
          </div>
        </Card>
      </Container>
      <EventStepCommentSection
        selectedStep={
          businessSteps?.find((x) => x.etapa_evento === selectedButton) ??
          templateWorkflowStepsArray[0]
        }
        stepLabel={determineArrowLabel(
          businessSteps?.find((x) => x.etapa_evento === selectedButton) ??
            templateWorkflowStepsArray[0],
          mostRecentStep,
          skippedStep
        )}
      />
    </>
  );
}

export default EventTimelineComponent;
